import React from "react";
import { ConversationStatus, getStatusConfig } from "@/types/conversation";
import { AlertTriangle, Clock, CheckCircle, Archive, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface StatusIndicatorProps {
  status: ConversationStatus | null | undefined;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const iconMap = {
  'circle': Circle,
  'alert-triangle': AlertTriangle,
  'clock': Clock,
  'check-circle': CheckCircle,
  'archive': Archive,
};

export function StatusIndicator({ 
  status, 
  className,
  size = "md" 
}: StatusIndicatorProps) {
  const config = getStatusConfig(status);
  
  // Get the icon component
  const IconComponent = config.icon ? iconMap[config.icon as keyof typeof iconMap] : Circle;
  
  // Size variations for the container
  const containerSizes = {
    sm: "w-6 h-6",
    md: "w-8 h-8", 
    lg: "w-10 h-10"
  };
  
  // Size variations for the icon
  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  // Extract background and text colors from the className
  const getIndicatorStyles = (statusConfig: typeof config) => {
    // Convert badge styles to indicator styles
    if (statusConfig.className.includes('bg-blue-100')) {
      return 'bg-blue-100 text-blue-600 border-blue-200';
    }
    if (statusConfig.className.includes('bg-red-100')) {
      return 'bg-red-100 text-red-600 border-red-200';
    }
    if (statusConfig.className.includes('bg-yellow-100')) {
      return 'bg-yellow-100 text-yellow-600 border-yellow-200';
    }
    if (statusConfig.className.includes('bg-green-100')) {
      return 'bg-green-100 text-green-600 border-green-200';
    }
    if (statusConfig.className.includes('bg-gray-100')) {
      return 'bg-gray-100 text-gray-600 border-gray-200';
    }
    // Default fallback
    return 'bg-blue-100 text-blue-600 border-blue-200';
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className={cn(
            "rounded-full flex items-center justify-center border",
            containerSizes[size],
            getIndicatorStyles(config),
            "transition-colors cursor-help",
            className
          )}
        >
          {IconComponent && (
            <IconComponent className={iconSizes[size]} />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{config.label}</p>
      </TooltipContent>
    </Tooltip>
  );
} 